/**
 * Cache Manager Utility
 * Provides centralized cache invalidation and management
 */

class CacheManager {
    constructor() {
        this.caches = new Map();
        this.listeners = new Map();
    }

    /**
     * Register a cache instance
     * @param {string} name - Cache identifier
     * @param {Object} cacheInstance - Cache object with clear/invalidate method
     */
    register(name, cacheInstance) {
        this.caches.set(name, cacheInstance);
    }

    /**
     * Invalidate specific cache by name
     * @param {string} name - Cache identifier
     */
    invalidate(name) {
        const cache = this.caches.get(name);
        if (cache) {
            if (typeof cache.clear === 'function') {
                cache.clear();
            } else if (cache instanceof Map) {
                cache.clear();
            } else if (cache.data !== undefined) {
                cache.data = null;
                cache.timestamp = null;
            }
            console.log(`Cache invalidated: ${name}`);
            this.notifyListeners(name);
        }
    }

    /**
     * Invalidate all registered caches
     */
    invalidateAll() {
        this.caches.forEach((cache, name) => {
            this.invalidate(name);
        });
        console.log('All caches invalidated');
    }

    /**
     * Invalidate caches by pattern
     * @param {RegExp|string} pattern - Pattern to match cache names
     */
    invalidatePattern(pattern) {
        const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
        this.caches.forEach((cache, name) => {
            if (regex.test(name)) {
                this.invalidate(name);
            }
        });
    }

    /**
     * Add listener for cache invalidation events
     * @param {string} cacheName - Cache to listen to
     * @param {Function} callback - Callback function
     */
    onInvalidate(cacheName, callback) {
        if (!this.listeners.has(cacheName)) {
            this.listeners.set(cacheName, []);
        }
        this.listeners.get(cacheName).push(callback);
    }

    /**
     * Notify listeners of cache invalidation
     * @param {string} cacheName - Cache that was invalidated
     */
    notifyListeners(cacheName) {
        const callbacks = this.listeners.get(cacheName) || [];
        callbacks.forEach(cb => cb(cacheName));
    }

    /**
     * Remove all caches and listeners
     */
    reset() {
        this.caches.clear();
        this.listeners.clear();
    }
}

// Export singleton instance
export const cacheManager = new CacheManager();

/**
 * Cache invalidation helpers for common operations
 */
export const invalidateCache = {
    // Invalidate service-related caches
    services: () => {
        cacheManager.invalidatePattern(/service/i);
    },

    // Invalidate batch-related caches
    batches: () => {
        cacheManager.invalidatePattern(/batch/i);
    },

    // Invalidate staff-related caches
    staff: () => {
        cacheManager.invalidatePattern(/staff/i);
    },

    // Invalidate user-related caches
    users: () => {
        cacheManager.invalidate('subscribedUsers');
    },

    // Invalidate all caches
    all: () => {
        cacheManager.invalidateAll();
    },
};

export default cacheManager;
